import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Calculator, BookOpen, List, Wheat, Flame, AlertTriangle, Lightbulb } from 'lucide-react';

/* ─── FAQs ───────────────────────────────────────────── */

const faqs = [
  {
    q: '¿Cual es el food cost ideal en una panaderia artesana?',
    a: 'El food cost ideal en una panaderia artesana oscila entre el 25% y el 35%, dependiendo del tipo de producto. Los panes basicos (barra, hogaza) tienen un food cost mas bajo (20-28%) porque la harina es barata, mientras que la bolleria con mantequilla (croissants, brioches) sube al 30-38% por el coste de la materia grasa. El food cost global de un obrador bien gestionado deberia estar entre el 28% y el 32%.',
  },
  {
    q: '¿Como calculo la merma de la harina en el obrador?',
    a: 'La harina tiene una merma directa muy baja (1-2% por polvo residual en el saco y en la amasadora), pero hay que considerar la merma indirecta: la harina que usas para enharinar la mesa de trabajo, los banneton y las palas del horno. En un obrador que produce 200 piezas al dia, esta merma indirecta puede representar 0,5-1 kg de harina diario. Pesala y contabilizala.',
  },
  {
    q: '¿Debo incluir el coste del horno en el escandallo?',
    a: 'Si, y es uno de los errores mas comunes no hacerlo. El coste energetico del horno (gas o electricidad) puede representar entre el 5% y el 12% del coste total de produccion de una panaderia. Calcula el consumo por horneada dividiendo la factura energetica mensual entre el numero de horneadas. Incluye ese coste como un ingrediente mas en cada escandallo.',
  },
  {
    q: '¿Cuantas barras de pan salen de un saco de harina de 25 kg?',
    a: 'Depende del tipo de pan y de la hidratacion. Con una hidratacion estandar del 65% para barra de pan: 25 kg de harina + 16,25 L de agua + sal + levadura = ~41 kg de masa. Con un peso por pieza de 350 g (que tras hornear queda en ~300 g por perdida de humedad), salen aproximadamente 117 barras. Con masa madre e hidratacion alta (80%), la proporcion cambia y pueden salir mas piezas pero de textura diferente.',
  },
  {
    q: '¿Que pesa mas en el escandallo de una panaderia: ingredientes o energia?',
    a: 'En la mayoria de panaderias artesanas, los ingredientes representan el 60-70% del coste de produccion, la energia el 12-18%, la mano de obra el 15-25% y los envases/packaging el 2-5%. Sin embargo, en bolleria con mantequilla (croissants, brioches, danes), el ingrediente graso puede disparar la partida de materia prima hasta el 75-80% del coste total.',
  },
  {
    q: '¿Como afecta la fermentacion larga al coste del pan?',
    a: 'La fermentacion larga (12-24 horas en frio) aumenta el coste energetico de la nevera (0,02-0,05 euros por pieza), pero reduce el coste de levadura (se usa menos, de 20 g/kg a 5 g/kg) y mejora drasticamente el sabor y la conservacion del pan. En el escandallo final, el sobrecoste de la fermentacion larga es minimo (0,03-0,08 euros por pieza) y se compensa facilmente con un PVP ligeramente superior justificado por la calidad.',
  },
  {
    q: '¿Como calculo el coste de la masa madre?',
    a: 'La masa madre no se "compra" como la levadura, pero tiene coste de mantenimiento: cada refresco consume harina y agua. Un refresco tipico usa 100 g de harina + 100 g de agua al dia. Con harina a 0,85 euros/kg, el coste diario de mantener la masa madre es de ~0,09 euros. Si produces 100 panes al dia con esa masa madre, el coste por pieza es de 0,001 euros: practicamente irrelevante. Incluye la harina de refresco en tu escandallo como coste fijo diario.',
  },
  {
    q: '¿Merece la pena usar mantequilla premium en croissants?',
    a: 'Depende de tu posicionamiento. La mantequilla estandar cuesta ~8,50 euros/kg y la premium (tipo Beurre d\'Isigny AOP, 84% MG) cuesta ~14-16 euros/kg. En un croissant que lleva 30 g de mantequilla laminada, la diferencia es de 0,17-0,23 euros por pieza. Si vendes el croissant a 2,00 euros, la mantequilla premium sube el food cost en un 8-11%. Si lo vendes a 2,80-3,00 euros como "croissant de mantequilla premium", el margen mejora y el cliente percibe la diferencia de calidad en sabor, hojaldrado y aroma.',
  },
];

/* ─── Schema.org JSON-LD ─────────────────────────────── */

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

const articleJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Escandallo de Panaderia: Como Calcular el Coste Real de Cada Pan y Bolleria',
  description: 'Guia completa para calcular escandallos en panaderias y obradores. Ejemplos reales de barra de pan artesano, croissant y pan de masa madre con ingredientes, mermas, coste energetico del horno y food cost objetivo.',
  author: { '@type': 'Organization', name: 'PLEKO' },
  publisher: { '@type': 'Organization', name: 'PLEKO', logo: { '@type': 'ImageObject', url: 'https://pleko.app/favicon.svg' } },
  datePublished: '2026-06-18',
  dateModified: '2026-06-18',
  image: 'https://pleko.app/chef-hero.jpg',
};

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'PLEKO', item: 'https://pleko.app/' },
    { '@type': 'ListItem', position: 2, name: 'Escandallo', item: 'https://pleko.app/escandallo' },
    { '@type': 'ListItem', position: 3, name: 'Escandallo de Panaderia', item: 'https://pleko.app/escandallo-panaderia' },
  ],
};

/* ─── Table of Contents ──────────────────────────────── */

const toc = [
  { id: 'por-que-diferente', label: '¿Por que el escandallo es diferente en panaderia?' },
  { id: 'ingredientes-mermas', label: 'Ingredientes tipicos y sus mermas' },
  { id: 'ejemplo-barra', label: 'Ejemplo: escandallo de barra de pan artesano' },
  { id: 'ejemplo-croissant', label: 'Ejemplo: escandallo de croissant de mantequilla' },
  { id: 'ejemplo-masa-madre', label: 'Ejemplo: escandallo de pan de masa madre' },
  { id: 'coste-horno', label: 'Como calcular el coste energetico del horno' },
  { id: 'food-cost-optimo', label: 'Food cost optimo en panaderia' },
  { id: 'errores-comunes', label: 'Errores comunes al escandallar en panaderia' },
  { id: 'software-vs-excel', label: 'Software vs Excel para obradores' },
  { id: 'faqs', label: 'Preguntas frecuentes' },
];

/* ─── Component ──────────────────────────────────────── */

export default function EscandalloPanaderiaPage() {
  return (
    <>
      <Helmet>
        <title>Escandallo de Panaderia — Como Calcular el Coste de Pan y Bolleria | PLEKO</title>
        <meta
          name="description"
          content="Guia completa para calcular escandallos en panaderias y obradores. Ejemplos reales de barra de pan artesano, croissant y pan de masa madre con ingredientes, mermas, coste energetico del horno y food cost objetivo."
        />
        <meta
          name="keywords"
          content="escandallo panaderia, escandallo obrador, costes panaderia, food cost panaderia, escandallo pan, coste pan artesano, escandallo croissant, escandallo bolleria, coste produccion pan, food cost obrador, mermas panaderia, coste horno panaderia, escandallo masa madre"
        />
        <link rel="canonical" href="https://pleko.app/escandallo-panaderia" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://pleko.app/escandallo-panaderia" />
        <meta property="og:title" content="Escandallo de Panaderia — Como Calcular el Coste Real de Pan y Bolleria" />
        <meta property="og:description" content="Guia completa para calcular escandallos en panaderias y obradores. Ejemplos reales con ingredientes, mermas, coste energetico y food cost objetivo." />
        <meta property="og:image" content="https://pleko.app/chef-hero.jpg" />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Escandallo de Panaderia — Coste Real de Pan y Bolleria | PLEKO" />
        <meta name="twitter:description" content="Calcula el food cost de tu obrador con 3 ejemplos reales: barra artesana, croissant y pan de masa madre. Incluye mermas, coste energetico del horno y errores comunes." />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <div className="bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-b from-amber-50 to-white py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium mb-6">
                <Wheat className="w-4 h-4" />
                Guia especializada para panaderias y obradores — 2026
              </span>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">
                Escandallo de <span className="text-amber-600">Panaderia</span>: Como Calcular el Coste Real de Cada Pan y Bolleria
              </h1>
              <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
                La panaderia tiene particularidades que la hacen unica a la hora de escandallar: fermentaciones largas, mermas por horneado, costes energeticos del horno y la variabilidad de la masa madre. Aprende a calcular el coste real de cada pieza con ejemplos detallados.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="#ejemplo-barra"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-amber-600 text-white font-semibold rounded-xl hover:bg-amber-700 transition-colors shadow-lg shadow-amber-200"
                >
                  Ver ejemplos de escandallos
                  <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/calculadora-escandallo"
                  className="inline-flex items-center gap-2 px-6 py-3 border-2 border-slate-200 text-slate-700 font-semibold rounded-xl hover:border-slate-300 transition-colors"
                >
                  <Calculator className="w-4 h-4" />
                  Calculadora de costes
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

          {/* Table of Contents */}
          <nav className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-12">
            <div className="flex items-center gap-2 mb-4">
              <List className="w-5 h-5 text-amber-600" />
              <h2 className="text-lg font-bold text-slate-900">Contenido de esta guia</h2>
            </div>
            <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {toc.map((item, i) => (
                <li key={item.id}>
                  <a href={`#${item.id}`} className="text-sm text-amber-600 hover:text-amber-800 hover:underline">
                    {i + 1}. {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="prose prose-lg max-w-none">

            {/* Section 1: Por que es diferente */}
            <h2 id="por-que-diferente" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">¿Por que el escandallo es diferente en panaderia?</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El <strong>escandallo en una panaderia</strong> comparte la base del <Link to="/escandallo" className="text-amber-600 hover:underline">escandallo clasico de cocina</Link>, pero tiene particularidades que lo convierten en un ejercicio distinto. En un restaurante, el escandallo se centra en ingredientes caros (carnes, pescados, mariscos) con mermas de limpieza y coccion. En una panaderia, los ingredientes son baratos individualmente (harina, agua, sal, levadura), pero el <strong>volumen de produccion y los costes energeticos</strong> hacen que los centimos importen enormemente.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Una panaderia artesana que produce 500 piezas al dia y no controla un sobrecoste de 0,05 euros por pieza esta perdiendo <strong>25 euros diarios</strong>, 750 euros al mes, <strong>9.000 euros al ano</strong>. Por eso, el escandallo en panaderia no es un lujo: es la diferencia entre un obrador rentable y uno que malvive.
            </p>

            <div className="bg-amber-50 border border-amber-100 rounded-xl p-6 mb-8">
              <p className="text-amber-800 font-medium text-center text-lg">
                3 factores unicos del escandallo en panaderia
              </p>
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-600">Fermentacion</p>
                  <p className="text-sm text-slate-600 mt-1">Tiempos largos que afectan al coste de nevera y mano de obra</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-600">Horno</p>
                  <p className="text-sm text-slate-600 mt-1">Coste energetico del 5-12% del total de produccion</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-600">Merma horno</p>
                  <p className="text-sm text-slate-600 mt-1">Perdida de peso del 12-18% por evaporacion de agua</p>
                </div>
              </div>
            </div>

            <h3 className="text-xl font-bold text-slate-800 mb-4">5 claves del escandallo de panaderia</h3>
            <ul className="space-y-3 mb-8">
              {[
                'Los ingredientes son baratos pero el volumen amplifica cualquier error de calculo',
                'La merma de horneado (12-18% de perdida de peso) es la mayor merma del obrador',
                'El coste energetico del horno debe incluirse como "ingrediente" en cada escandallo',
                'La fermentacion larga reduce el coste de levadura pero aumenta el coste de frio',
                'La masa madre tiene coste de mantenimiento diario que debe repartirse entre las piezas',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            {/* Section 2: Ingredientes y mermas */}
            <h2 id="ingredientes-mermas" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">Ingredientes Tipicos de Panaderia y Sus Mermas</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              En panaderia, las mermas no son de limpieza (como pelar gambas o deshuesar carne), sino de <strong>proceso</strong>: lo que se pierde en el amasado, la fermentacion y, sobre todo, el horneado. La tabla siguiente recoge los ingredientes principales de un obrador con sus mermas tipicas y precios de referencia en Espana (junio 2026).
            </p>

            <div className="overflow-x-auto mb-10">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-amber-800 text-white">
                    <th className="p-3 text-left font-medium">Ingrediente</th>
                    <th className="p-3 text-center font-medium">Precio referencia</th>
                    <th className="p-3 text-center font-medium">Merma tipica</th>
                    <th className="p-3 text-left font-medium">Causa de la merma</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Harina de fuerza (W300+)', '0,85 €/kg', '1-2%', 'Polvo residual en saco y amasadora, enharinado de mesa'],
                    ['Harina de trigo floja', '0,75 €/kg', '1-2%', 'Polvo residual, enharinado de bannetons'],
                    ['Levadura fresca prensada', '3,50 €/kg', '3-5%', 'Producto perecedero, restos en envase, caducidad'],
                    ['Levadura seca instantanea', '12,00 €/kg', '0%', 'Envase cerrado, larga conservacion'],
                    ['Mantequilla (82% MG)', '8,50 €/kg', '2-3%', 'Restos en papel/envase, perdida en laminado'],
                    ['Mantequilla seca (84% MG)', '14,00 €/kg', '2-3%', 'Restos en papel/envase, perdida en laminado'],
                    ['Huevos (L)', '0,22 €/ud', '10-12%', 'Cascara (10%), restos en cascara, yemas rotas'],
                    ['Azucar blanco', '1,10 €/kg', '0-1%', 'Minima, polvo residual'],
                    ['Sal fina', '0,35 €/kg', '0%', 'Sin merma apreciable'],
                    ['Aceite de oliva virgen extra', '8,50 €/l', '1-2%', 'Restos en recipiente, salpicaduras'],
                    ['Leche entera', '0,95 €/l', '1-2%', 'Restos en envase, derrames'],
                    ['Chocolate cobertura 55%', '9,50 €/kg', '2-3%', 'Restos en bol de templado, merma de temperado'],
                  ].map(([ingrediente, precio, merma, causa], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{ingrediente}</td>
                      <td className="p-3 border-b border-slate-100 text-center font-semibold text-amber-700">{precio}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-slate-600">{merma}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600 text-sm">{causa}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              Observa como las mermas de ingredientes en panaderia son bajas (0-5% en la mayoria). La merma critica no esta en los ingredientes: esta en el <strong>horneado</strong>, donde cada pieza pierde entre un 12% y un 18% de peso por evaporacion de agua. Esto significa que una barra que pesa 350 g antes de entrar al horno, sale pesando 290-310 g. Si vendes pan al peso, esta merma es la que mas impacta tu margen.
            </p>

            {/* Section 3: Ejemplo barra de pan */}
            <h2 id="ejemplo-barra" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">Ejemplo Completo: Escandallo de Barra de Pan Artesano</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Barra de pan artesano de 250 g (peso final tras hornear), elaborada con harina de fuerza, fermentacion mixta (poolish + levadura), horneada en horno de solera refractaria a 240 grados C. <strong>Produccion de referencia: 100 barras por hornada.</strong>
            </p>
            <div className="overflow-x-auto mb-4">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-amber-800 text-white">
                    <th className="p-3 text-left font-medium">Ingrediente</th>
                    <th className="p-3 text-right font-medium">Cantidad</th>
                    <th className="p-3 text-left font-medium">Unidad</th>
                    <th className="p-3 text-right font-medium">Precio/unidad</th>
                    <th className="p-3 text-right font-medium">Merma %</th>
                    <th className="p-3 text-right font-medium">Coste neto</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Harina de fuerza (W300)', '175 g', 'g', '0,85 €/kg', '2%', '0,152 €'],
                    ['Agua', '114 g', 'g', '0,002 €/l', '0%', '0,000 €'],
                    ['Sal fina', '3,5 g', 'g', '0,35 €/kg', '0%', '0,001 €'],
                    ['Levadura fresca', '3 g', 'g', '3,50 €/kg', '5%', '0,011 €'],
                    ['Harina poolish (pre-fermento)', '25 g', 'g', '0,85 €/kg', '2%', '0,022 €'],
                    ['Aceite de oliva (engrasado bandeja)', '2 ml', 'ml', '8,50 €/l', '0%', '0,017 €'],
                    ['Coste energetico horno (por pieza)', '—', '—', '—', '—', '0,045 €'],
                  ].map(([ing, qty, unit, precio, merma, coste], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{ing}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{qty}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600">{unit}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{precio}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{merma}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-medium text-slate-800">{coste}</td>
                    </tr>
                  ))}
                  <tr className="bg-amber-50">
                    <td colSpan={5} className="p-3 font-bold text-slate-800">COSTE TOTAL POR BARRA (250 g)</td>
                    <td className="p-3 text-right font-bold text-amber-700 text-lg">0,248 €</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Coste por barra</p>
                <p className="text-2xl font-bold text-amber-700">0,248 €</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">PVP</p>
                <p className="text-2xl font-bold text-slate-800">1,10 €</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Food cost</p>
                <p className="text-2xl font-bold text-green-600">22,5%</p>
                <p className="text-xs text-green-600 font-medium">Excelente</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8">
              <p className="text-amber-800 font-semibold mb-1">Consejo profesional</p>
              <p className="text-slate-700 text-sm">
                La barra de pan artesano es el producto con <strong>mejor margen</strong> de una panaderia, pero tambien el de mayor competencia en precio. Para mejorar el margen, trabaja dos ejes: negocia el precio de la harina comprando por palet completo (25 sacos) y optimiza las horneadas para llenar el horno al maximo (reduce el coste energetico por pieza). La diferencia entre un horno lleno y uno a media carga puede ser de 0,02 euros por pieza.
              </p>
            </div>

            {/* Section 4: Ejemplo croissant */}
            <h2 id="ejemplo-croissant" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">Ejemplo: Escandallo de Croissant de Mantequilla</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Croissant clasico frances de 70 g (peso final), elaborado con mantequilla seca (84% MG) laminada a 3 vueltas dobles. El croissant es el producto <strong>mas rentable de la vitrina</strong> si se controla el coste de la mantequilla, que representa mas del 50% del coste total de materia prima.
            </p>
            <div className="overflow-x-auto mb-4">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-yellow-800 text-white">
                    <th className="p-3 text-left font-medium">Ingrediente</th>
                    <th className="p-3 text-right font-medium">Cantidad</th>
                    <th className="p-3 text-left font-medium">Unidad</th>
                    <th className="p-3 text-right font-medium">Precio/unidad</th>
                    <th className="p-3 text-right font-medium">Merma %</th>
                    <th className="p-3 text-right font-medium">Coste neto</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Harina de fuerza (W350)', '42 g', 'g', '0,95 €/kg', '2%', '0,041 €'],
                    ['Mantequilla seca 84% MG', '30 g', 'g', '14,00 €/kg', '3%', '0,433 €'],
                    ['Azucar blanco', '5 g', 'g', '1,10 €/kg', '0%', '0,006 €'],
                    ['Sal fina', '1 g', 'g', '0,35 €/kg', '0%', '0,000 €'],
                    ['Levadura fresca', '2,5 g', 'g', '3,50 €/kg', '5%', '0,009 €'],
                    ['Leche entera', '10 ml', 'ml', '0,95 €/l', '2%', '0,010 €'],
                    ['Huevo (doradura)', '3 g', 'g', '3,70 €/kg', '0%', '0,011 €'],
                    ['Coste energetico horno (por pieza)', '—', '—', '—', '—', '0,035 €'],
                  ].map(([ing, qty, unit, precio, merma, coste], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{ing}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{qty}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600">{unit}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{precio}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{merma}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-medium text-slate-800">{coste}</td>
                    </tr>
                  ))}
                  <tr className="bg-yellow-50">
                    <td colSpan={5} className="p-3 font-bold text-slate-800">COSTE TOTAL POR CROISSANT (70 g)</td>
                    <td className="p-3 text-right font-bold text-yellow-700 text-lg">0,545 €</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Coste por croissant</p>
                <p className="text-2xl font-bold text-yellow-700">0,545 €</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">PVP</p>
                <p className="text-2xl font-bold text-slate-800">1,80 €</p>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Food cost</p>
                <p className="text-2xl font-bold text-yellow-600">30,3%</p>
                <p className="text-xs text-yellow-600 font-medium">Vigilar</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
              <p className="text-amber-800 font-semibold mb-1">Mantequilla estandar vs premium</p>
              <p className="text-slate-700 text-sm">
                Si usas mantequilla estandar (82% MG) a 8,50 euros/kg en vez de seca (84% MG) a 14,00 euros/kg, el coste del croissant baja a <strong>0,38 euros</strong> y el food cost al <strong>21,1%</strong>. La diferencia de calidad es notable en hojaldrado y sabor, pero si tu PVP es de 1,80 euros, la mantequilla estandar da mas margen. Si vendes el croissant como "premium" a 2,50-2,80 euros, la mantequilla seca se justifica.
              </p>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              La mantequilla representa el <strong>79% del coste de ingredientes</strong> del croissant. Por eso, cualquier fluctuacion en el precio de la mantequilla impacta directamente en el margen. Si el precio de la mantequilla sube un 10% (de 14,00 a 15,40 euros/kg), el food cost del croissant pasa del 30,3% al 32,7%. Controlar este precio es critico.
            </p>

            {/* Section 5: Ejemplo pan de masa madre */}
            <h2 id="ejemplo-masa-madre" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">Ejemplo: Escandallo de Pan de Masa Madre</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Hogaza de pan de masa madre de 500 g (peso final), con fermentacion en frio de 18 horas. El pan de masa madre tiene un <strong>coste de ingredientes mas bajo</strong> que el pan con levadura (no compras levadura), pero requiere un coste adicional de fermentacion en frio y un mantenimiento diario de la masa madre.
            </p>
            <div className="overflow-x-auto mb-4">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-stone-800 text-white">
                    <th className="p-3 text-left font-medium">Ingrediente</th>
                    <th className="p-3 text-right font-medium">Cantidad</th>
                    <th className="p-3 text-left font-medium">Unidad</th>
                    <th className="p-3 text-right font-medium">Precio/unidad</th>
                    <th className="p-3 text-right font-medium">Merma %</th>
                    <th className="p-3 text-right font-medium">Coste neto</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Harina de fuerza ecologica', '310 g', 'g', '1,60 €/kg', '2%', '0,506 €'],
                    ['Harina integral de centeno', '40 g', 'g', '1,40 €/kg', '2%', '0,057 €'],
                    ['Agua filtrada', '245 g', 'g', '0,002 €/l', '0%', '0,001 €'],
                    ['Sal marina', '7 g', 'g', '0,80 €/kg', '0%', '0,006 €'],
                    ['Masa madre activa', '80 g', 'g', '~0,001 €/g', '0%', '0,080 €'],
                    ['Harina para enharinado (banneton)', '5 g', 'g', '0,85 €/kg', '0%', '0,004 €'],
                    ['Coste fermentacion frio (18h)', '—', '—', '—', '—', '0,065 €'],
                    ['Coste energetico horno (por pieza)', '—', '—', '—', '—', '0,060 €'],
                  ].map(([ing, qty, unit, precio, merma, coste], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{ing}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{qty}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600">{unit}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{precio}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{merma}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-medium text-slate-800">{coste}</td>
                    </tr>
                  ))}
                  <tr className="bg-stone-100">
                    <td colSpan={5} className="p-3 font-bold text-slate-800">COSTE TOTAL POR HOGAZA (500 g)</td>
                    <td className="p-3 text-right font-bold text-stone-700 text-lg">0,779 €</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div className="bg-stone-100 border border-stone-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Coste por hogaza</p>
                <p className="text-2xl font-bold text-stone-700">0,779 €</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">PVP</p>
                <p className="text-2xl font-bold text-slate-800">3,90 €</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Food cost</p>
                <p className="text-2xl font-bold text-green-600">20,0%</p>
                <p className="text-xs text-green-600 font-medium">Excelente</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8">
              <p className="text-amber-800 font-semibold mb-1">Consejo profesional</p>
              <p className="text-slate-700 text-sm">
                El pan de masa madre con harina ecologica tiene un <strong>food cost del 20%: excelente</strong>. Aun asi, el coste de la harina ecologica (1,60 euros/kg vs 0,85 euros/kg convencional) duplica la partida de harina. Si tu clientela valora lo ecologico (y paga 3,90 euros por una hogaza), la inversion se justifica. Si no, puedes usar harina convencional de fuerza: el coste total baja a 0,52 euros y el food cost al 13,3%.
              </p>
            </div>

            {/* Section 6: Coste energetico del horno */}
            <h2 id="coste-horno" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">Como Calcular el Coste Energetico del Horno</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El <strong>coste del horno</strong> es el gran olvidado de los escandallos de panaderia. Muchos obradores no lo incluyen porque "es un coste fijo". Pero no lo es: cuantas mas horneadas hagas, mas gas o electricidad consumes. Y el coste puede representar entre un <strong>5% y un 12%</strong> del coste total de produccion.
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-red-800 text-white">
                    <th className="p-3 text-left font-medium">Tipo de horno</th>
                    <th className="p-3 text-center font-medium">Consumo por horneada</th>
                    <th className="p-3 text-center font-medium">Coste por horneada</th>
                    <th className="p-3 text-center font-medium">Piezas por horneada</th>
                    <th className="p-3 text-center font-medium">Coste por pieza</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Horno de solera refractaria (gas)', '~3 m3 gas', '~4,50 €', '80-120 piezas', '0,038-0,056 €'],
                    ['Horno rotativo (gas)', '~4,5 m3 gas', '~6,75 €', '150-200 piezas', '0,034-0,045 €'],
                    ['Horno de solera (electrico)', '~15 kWh', '~3,75 €', '80-120 piezas', '0,031-0,047 €'],
                    ['Horno rotativo (electrico)', '~22 kWh', '~5,50 €', '150-200 piezas', '0,028-0,037 €'],
                    ['Horno de lena', '~8 kg lena', '~2,40 €', '60-80 piezas', '0,030-0,040 €'],
                  ].map(([tipo, consumo, coste, piezas, costePieza], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{tipo}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-slate-600">{consumo}</td>
                      <td className="p-3 border-b border-slate-100 text-center font-semibold text-red-700">{coste}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-slate-600">{piezas}</td>
                      <td className="p-3 border-b border-slate-100 text-center font-semibold text-amber-700">{costePieza}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-xl p-5 mb-6">
              <div className="flex items-start gap-3">
                <Flame className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-800 mb-1">Formula para calcular el coste por pieza</p>
                  <p className="text-slate-700 text-sm leading-relaxed">
                    <strong>Coste por pieza = (Consumo energetico por horneada x Precio kWh o m3) / Numero de piezas por horneada</strong>. Ejemplo: horno de gas con consumo de 3 m3/horneada, precio del gas a 1,50 euros/m3, 100 barras por horneada: (3 x 1,50) / 100 = 0,045 euros/barra. Ajusta con tu factura real de gas o electricidad para mayor precision.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              La clave para reducir el coste energetico: <strong>llenar el horno al maximo</strong>. Un horno a media carga consume practicamente lo mismo que uno lleno, pero reparte el coste entre la mitad de piezas. Planifica las horneadas para maximizar la capacidad y agrupa productos similares (misma temperatura, mismo tiempo).
            </p>

            {/* Section 7: Food cost optimo */}
            <h2 id="food-cost-optimo" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">Food Cost Optimo en Panaderia</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El <strong>food cost en panaderia</strong> funciona de forma diferente al de un restaurante. En un restaurante, el objetivo es estar por debajo del 30-33%. En una panaderia, los rangos son distintos porque los margenes unitarios son menores pero el volumen es mucho mayor. Consulta tambien nuestra guia general de <Link to="/food-cost" className="text-amber-600 hover:underline">food cost en hosteleria</Link>.
            </p>

            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Tipo de producto</th>
                    <th className="p-3 text-center font-medium">Food cost objetivo</th>
                    <th className="p-3 text-center font-medium">Margen tipico</th>
                    <th className="p-3 text-left font-medium">Comentario</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Pan basico (barra, chapata)', '20-28%', 'Medio', 'Producto gancho. Margen unitario bajo, alto volumen', 'bg-green-50'],
                    ['Pan artesano (masa madre, hogaza)', '18-25%', 'Alto', 'PVP mas alto justifica harina premium', 'bg-green-50'],
                    ['Pan especial (semillas, nueces, aceitunas)', '25-32%', 'Medio', 'Ingredientes caros pero PVP alto', 'bg-yellow-50'],
                    ['Croissant / bolleria laminada', '28-35%', 'Medio', 'Mantequilla encarece. Controlar gramaje', 'bg-yellow-50'],
                    ['Bolleria rellena (napolitana, palmera)', '25-30%', 'Medio-alto', 'Rellenos economicos si son caseros', 'bg-green-50'],
                    ['Empanadas / salados', '30-38%', 'Bajo', 'Rellenos de proteina encarecen. Vigilar', 'bg-orange-50'],
                    ['Tartas y pasteles', '22-30%', 'Alto', 'Alto valor percibido, justifica PVP premium', 'bg-green-50'],
                    ['Galletas', '15-22%', 'Muy alto', 'Ingredientes baratos, larga conservacion', 'bg-green-50'],
                  ].map(([tipo, foodCost, margen, comentario, color], i) => (
                    <tr key={i} className={color}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{tipo}</td>
                      <td className="p-3 border-b border-slate-100 text-center font-semibold text-amber-700">{foodCost}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-slate-600">{margen}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600 text-sm">{comentario}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-amber-50 border border-amber-100 rounded-xl p-6 mb-8">
              <p className="text-amber-800 font-medium text-center text-lg mb-2">
                Regla practica para panaderias
              </p>
              <p className="text-slate-700 text-center">
                El <strong>food cost global del obrador</strong> (todos los productos ponderados por ventas) deberia estar entre el <strong>25% y el 32%</strong>. Si esta por debajo del 25%, probablemente estas cobrando demasiado o usando ingredientes de baja calidad. Si supera el 35%, necesitas revisar tus recetas, tus precios o tus proveedores.
              </p>
            </div>

            {/* Section 8: Errores comunes */}
            <h2 id="errores-comunes" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-2"><AlertTriangle className="w-7 h-7 text-red-500" /> Errores Comunes al Escandallar en Panaderia</span>
            </h2>

            <div className="space-y-6 mb-10">
              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">1. No incluir el coste del horno</h3>
                <p className="text-slate-700 leading-relaxed">
                  Es el error mas frecuente. El horno puede costar entre 0,03 y 0,06 euros por pieza. En un obrador que produce 500 piezas diarias, eso son 15-30 euros al dia de coste invisible. Al mes: 450-900 euros. Al ano: 5.400-10.800 euros. Incluye siempre el coste energetico como una linea mas del escandallo.
                </p>
              </div>

              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">2. Ignorar la merma de horneado</h3>
                <p className="text-slate-700 leading-relaxed">
                  Una masa de 300 g antes de hornear sale del horno pesando 250-260 g (perdida del 12-18% por evaporacion). Si vendes por peso, estas vendiendo menos producto del que has costado. Si vendes por pieza, la merma de horneado ya esta incluida, pero debes calcularla para saber el coste real por unidad vendida. Pesa siempre antes y despues del horno para cada tipo de producto.
                </p>
              </div>

              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">3. No contabilizar el pan no vendido</h3>
                <p className="text-slate-700 leading-relaxed">
                  El pan artesano tiene una vida util de 24-48 horas. Todo lo que no se vende el mismo dia es <strong>merma comercial</strong>. Si produces 200 barras y vendes 180, esas 20 barras son un 10% de merma que no aparece en el escandallo pero destruye tu margen. Lleva un registro diario de produccion vs ventas y ajusta la produccion para minimizar el pan sobrante. La merma comercial aceptable es del 3-5%.
                </p>
              </div>

              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">4. Usar el mismo escandallo para todos los tamanos</h3>
                <p className="text-slate-700 leading-relaxed">
                  Una hogaza de 1 kg no cuesta el doble que una de 500 g, porque el coste energetico por unidad es similar (mismo tiempo de horneado) y la merma de horneado proporcionalmente es menor en piezas grandes. Haz un escandallo especifico para cada formato y peso.
                </p>
              </div>

              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">5. No actualizar precios de harina y mantequilla</h3>
                <p className="text-slate-700 leading-relaxed">
                  La harina puede variar un 15-25% en un ano y la mantequilla un 20-30% segun la temporada y el mercado de commodities. Un escandallo de hace 6 meses con los precios de entonces te esta dando food costs falsos. Actualiza cada vez que recibas un albaran con precios nuevos. Con <Link to="/software-escandallo" className="text-amber-600 hover:underline">software de escandallos</Link> como PLEKO, esto se hace automaticamente al escanear la factura.
                </p>
              </div>
            </div>

            {/* Section 9: Software vs Excel */}
            <h2 id="software-vs-excel" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-2"><Lightbulb className="w-7 h-7 text-amber-500" /> Software vs Excel para Obradores</span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Muchas panaderias empiezan controlando costes con <Link to="/plantilla-escandallo-excel" className="text-amber-600 hover:underline">plantillas de Excel</Link>. Es un buen primer paso, pero tiene limitaciones importantes cuando el obrador crece:
            </p>

            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Aspecto</th>
                    <th className="p-3 text-center font-medium">Excel / Hoja de calculo</th>
                    <th className="p-3 text-center font-medium">Software especializado</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Actualizacion de precios', 'Manual, uno por uno', 'Automatica al escanear albaran (OCR)'],
                    ['Propagacion de costes', 'Manual si cambias un ingrediente', 'Automatica en todas las recetas'],
                    ['Coste energetico', 'Calculo manual aparte', 'Integrado como linea del escandallo'],
                    ['Merma de horneado', 'Formula manual', 'Preconfigurada por tipo de producto'],
                    ['Control de produccion vs ventas', 'Hoja separada', 'Dashboard integrado con alertas'],
                    ['Multiples obradores', 'Un archivo por obrador', 'Vista centralizada de todos'],
                    ['Precio', 'Gratis', 'Desde 49 euros/mes'],
                    ['Curva de aprendizaje', 'Media (requiere conocer Excel)', 'Baja (interfaz pensada para hosteleria)'],
                  ].map(([aspecto, excel, software], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{aspecto}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-slate-600">{excel}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-amber-700 font-medium">{software}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              Si produces menos de 10 productos diferentes y no cambias de proveedor con frecuencia, Excel es suficiente para empezar. Pero cuando el obrador crece por encima de 20 referencias, con multiples proveedores y precios que cambian cada semana, un <Link to="/software-escandallo" className="text-amber-600 hover:underline">software de escandallos</Link> se amortiza el primer mes. Con PLEKO, escaneas el albaran del proveedor con el movil y todos los escandallos se actualizan al instante con los nuevos precios.
            </p>
          </div>

          {/* FAQ */}
          <div id="faqs" className="mt-16 scroll-mt-20">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Preguntas frecuentes sobre escandallos de panaderia</h2>
            <div className="space-y-4">
              {faqs.map(({ q, a }) => (
                <details key={q} className="border border-slate-200 rounded-xl overflow-hidden">
                  <summary className="p-5 font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 flex justify-between items-center list-none">
                    {q}
                    <span className="text-slate-400 text-xl ml-4">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">{a}</div>
                </details>
              ))}
            </div>
          </div>

          {/* Internal links */}
          <div className="mt-12 p-6 bg-slate-50 rounded-xl border border-slate-200">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2"><BookOpen className="w-5 h-5 text-amber-600" /> Guias relacionadas</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { to: '/escandallo', label: 'Escandallo en hosteleria: guia completa' },
                { to: '/ejemplo-escandallo', label: '8 ejemplos reales de escandallos' },
                { to: '/como-hacer-escandallo', label: 'Como hacer un escandallo paso a paso' },
                { to: '/escandallo-bebidas', label: 'Escandallo de bebidas y cocteles' },
                { to: '/calculadora-escandallo', label: 'Calculadora de escandallo online gratis' },
                { to: '/software-escandallo', label: 'Software de escandallo para restaurantes' },
                { to: '/plantilla-escandallo-excel', label: 'Plantilla de escandallo en Excel (gratis)' },
                { to: '/food-cost', label: 'Food Cost: que es y como calcularlo' },
              ].map(({ to, label }) => (
                <Link key={to} to={to} className="flex items-center gap-2 text-sm text-amber-600 hover:text-amber-800 hover:underline">
                  <ArrowRight className="w-3 h-3" /> {label}
                </Link>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 bg-gradient-to-r from-amber-600 to-yellow-600 rounded-2xl p-8 text-center text-white">
            <Wheat className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-3">Calcula los escandallos de tu obrador en minutos</h2>
            <p className="text-amber-100 mb-6 max-w-xl mx-auto">
              PLEKO calcula automaticamente el escandallo de todos tus panes, bollos y masas, incluyendo costes energeticos del horno. Escanea el albaran del proveedor y todos los costes se actualizan al instante. Prueba gratis 14 dias.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-amber-700 font-bold rounded-xl hover:bg-amber-50 transition-colors"
            >
              Empezar gratis
              <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-3 text-amber-200 text-sm">Sin tarjeta de credito. Configurado en 2 minutos.</p>
          </div>
        </section>
      </div>
    </>
  );
}
